import { useMarket } from '@ecosystem/market-context-selection';
import { useAnalytics, useFeedback } from '@ecosystem/newron-client-lib';
import React, { useCallback, useContext, useEffect } from 'react';

import { NotificationToastType } from '../../../common';
import { UpsellContext } from '../../../state';
import { setIsModalOpen, setNotificationMessage } from '../../../state/action';
import { waitForElement } from '../../../utils';

import { NewronLogoImage } from './NewronLogoImage';

import './MarketQualtricsSlide.css';

const QualtricsFeedbackIdentifier = 'B2BQualtricsFeedbackIdentifier';
const QualtricsButtonsSelector = '.QSI__EmbeddedFeedbackContainer_TextButton';

export const MarketQualtricsSlide = (): React.ReactElement => {
  const {
    dispatch,
    state: { isModalOpen },
  } = useContext(UpsellContext);
  const { extendEmbeddedData } = useFeedback();
  const { market } = useMarket();
  const { track } = useAnalytics();

  const eventClick = useCallback(
    button => {
      track('Upsell overlay completed', { choice: button?.title });
      if (button?.title === 'Contact me about B2B') {
        dispatch(
          setNotificationMessage({
            message: 'Thank you for your request. We will be in touch with more information.',
            type: NotificationToastType.Success,
            timeout: 3000,
          })
        );
      }
      dispatch(setIsModalOpen(false));
    },
    // eslint-disable-next-line
    [dispatch, track]
  );

  const handleClick = useCallback(async () => {
    const elementExists = await waitForElement(QualtricsButtonsSelector);
    if (elementExists) {
      const buttons = document.querySelectorAll(QualtricsButtonsSelector);
      buttons.forEach((button: HTMLElement) => {
        button.onclick = () => {
          eventClick(button);
        };
      });
    }
  }, [eventClick]);

  useEffect(() => {
    if (isModalOpen) {
      extendEmbeddedData({
        market: `${market?.itemGroupId}/${market?.countryId}`,
      });
      handleClick();
    }
  }, [extendEmbeddedData, handleClick, isModalOpen, market]);

  return (
    <div className="market-qualtrics-slide text-left pt-0 px-8 pb-6" data-test-id="market-qualtrics-slide">
      <NewronLogoImage />
      <div className="relative">
        <p className="market-qualtrics-slide-head text-brand mb-4 font-bold ">
          Want to know more about the potential in B2B markets?
        </p>
        <p className="text-lg mb-20">
          GfK <span className="text-brand">MI Sales Tracking</span> and{' '}
          <span className="text-brand">MI Supply Chain</span> insights can help you make the most of this growth
          opportunity. Let us know if you are interested by clicking the button below and your account manager will
          reach out to you.
        </p>
        <p className="text-lg">
          You can opt-out from receiving further communication on this by contacting us. Please refer to{' '}
          <a href="/privacy-policy" target="_blank">
            Privacy Policy
          </a>{' '}
          for more information.
        </p>
      </div>
      <div className="flex justify-evenly" id={QualtricsFeedbackIdentifier} data-test-id="qualtrics-buttons" />
    </div>
  );
};
